import React, { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import useHideOnEscape from "./useHideOnEscape";

// Uma ressalva atras de um icone (spec do redesign, R7), e nao um `title`. Com o rato a nota aparece
// enquanto o ponteiro esta em cima e some quando sai; ao teclado aparece com o foco; ao toque, e a um leitor
// de ecra que carrega no botao, cada clique mostra ou esconde. Um clique do rato nao a prende aberta, e o
// foco que esse clique deixa no botao tambem nao. O `relative` segura a nota dentro do <main> (ver o
// DataTable).
const InfoTip = ({ text, label }) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [tapped, setTapped] = useState(false);
  // O tipo do ponteiro que carregou no botao, ate ao clique: separa o toque do rato, e o foco do teclado
  // do foco que um clique deixa. Num toque o browser imita tambem a entrada do rato, que se ignora.
  const pointer = useRef(null);
  const open = hovered || focused || tapped;
  const id = useId();
  const [shift, setShift] = useState(0);
  const noteRef = useRef(null);
  const close = useCallback(() => {
    setHovered(false);
    setFocused(false);
    setTapped(false);
  }, []);
  useHideOnEscape(open, close);

  // A nota abre a direita do icone. Se assim passasse da margem direita do <main>, que rola na horizontal
  // e a cortava ou fazia a pagina rolar (num telemovel o icone fica longe da esquerda), desloca-se para a
  // esquerda o excesso mais 8px, sem passar da margem esquerda do <main>. Sem <main> (testes) conta a
  // janela. Mede-se a posicao natural, por isso o deslocamento volta a zero ao fechar.
  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const note = noteRef.current.getBoundingClientRect();
    const main = noteRef.current.closest("main")?.getBoundingClientRect();
    const right = main?.right ?? document.documentElement.clientWidth;
    const left = main?.left ?? 0;
    setShift(Math.max(0, Math.min(note.right + 8 - right, note.left - left)));
  }, [open]);
  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => pointer.current !== "touch" && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        onPointerDown={(event) => {
          pointer.current = event.pointerType;
        }}
        onFocus={() => !pointer.current && setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setTapped(false);
          pointer.current = null;
        }}
        onClick={() => {
          if (pointer.current !== "mouse") setTapped((shown) => !shown);
          pointer.current = null;
        }}
        className="p-0.5 rounded"
        style={{ color: "var(--color-text-secondary)" }}
      >
        <Info className="w-4 h-4" aria-hidden="true" />
      </button>
      {open && (
        <span
          id={id}
          ref={noteRef}
          role="note"
          className="absolute left-0 top-full mt-1 z-[4] w-72 max-w-[calc(100vw-3rem)] rounded-lg border p-2 text-xs font-normal"
          style={{ left: -shift, backgroundColor: "var(--color-surface-raised)", borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default InfoTip;
